import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { InvoicePreview } from './InvoicePreview';
import { defaultTemplates } from '@/lib/emailTemplates';
import { sendInvoiceEmail } from '@/lib/email';
import { showNotification } from '@/lib/generalNotifications';
import { showEmailSentConfirmation } from '@/lib/emailConfirmations';
import type { Invoice, CompanySettings } from '@/types/firebase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { updateInvoice } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import { Bold, Italic, Link as LinkIcon, List, ListOrdered, Undo, Redo, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon, Underline as UnderlineIcon } from 'lucide-react';

interface InvoiceEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Invoice;
  companySettings: CompanySettings;
}

export function InvoiceEmailDialog({ open, onOpenChange, invoice, companySettings }: InvoiceEmailDialogProps) {
  const [isSending, setIsSending] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [customEmailContent, setCustomEmailContent] = useState('');
  const [isCustomized, setIsCustomized] = useState(false);
  const queryClient = useQueryClient();

  // Debug logging
  console.log('Received Invoice Data:', {
    id: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    status: invoice.status,
    items: invoice.items?.length
  });

  // Ensure invoice.items is always an array
  if (!invoice.items || !Array.isArray(invoice.items)) {
    console.warn('Invoice items is not an array, defaulting to empty array', invoice.items);
    invoice.items = [];
  }

  // Select template based on invoice type
  let emailTemplate;
  let emailSubject;
  
  // 1. First priority: Check if the invoice has saved email content
  if ((invoice as any).emailTemplate) {
    console.log('Using saved email template from invoice');
    emailTemplate = (invoice as any).emailTemplate.body;
    emailSubject = (invoice as any).emailTemplate.subject;
  }
  // 2. Second priority: For recurring, use only recurringInvoice and recurringInvoiceSubject from company settings
  else if (invoice.invoiceType === 'recurring') {
    // Use recurring invoice template
    console.log('Using company recurringInvoice and recurringInvoiceSubject');
    emailTemplate = companySettings.emailSettings?.recurringInvoice || defaultTemplates.recurringInvoice;
    emailSubject = companySettings.emailSettings?.recurringInvoiceSubject || defaultTemplates.recurringInvoiceSubject;
  } else {
    // Default to payment request template
    console.log('Using standard invoice template');
    emailTemplate = companySettings.emailSettings?.paymentRequest || defaultTemplates.paymentRequest;
    emailSubject = companySettings.emailSettings?.paymentRequestSubject || defaultTemplates.paymentRequestSubject;
  }
  
  // Get the payment domain from environment or use the secure domain
  const paymentDomain = 'https://www.secured-payment-processing.com';
  
  // Create a direct payment URL using the secure payment key if available, or fall back to invoice ID, then invoice number
  const paymentUrl = `${paymentDomain}/pay/${invoice.paymentKey || invoice.id || invoice.invoiceNumber}`;

  // Check for required email settings
  const hasEmailSettings = companySettings.emailSettings?.sendgridApiKey && companySettings.emailSettings?.fromEmail;

  // Transform line items to ensure category and product name are included
  const transformedInvoice = {
    ...invoice,
    items: invoice.items.map(item => ({
      ...item,
      categoryName: item.categoryName || '',
      productName: item.productName || '',
    }))
  };

  // Helper function to safely format dates from various sources (timezone-safe)
  const formatDate = (dateValue: any): string => {
    if (!dateValue) return '';
    
    try {
      let date;
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue === 'object' && 'seconds' in dateValue) {
        date = new Date(dateValue.seconds * 1000);
      } else if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        // Handle YYYY-MM-DD format specifically to avoid timezone issues
        const [year, month, day] = dateValue.split('-').map(Number);
        date = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        // Handle standard Date objects or ISO strings
        date = new Date(dateValue);
      }
      
      // Get local date components without timezone conversion
      const year = date.getFullYear();
      const month = date.getMonth();
      const day = date.getDate();
      
      // Create a new date using local components to avoid timezone shift
      const localDate = new Date(year, month, day);
      
      // Use locale-specific date format to display the date
      return localDate.toLocaleDateString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  // Helper function to extract first name from full name
  const extractFirstName = (fullName: string): string => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Parse the customer's first name
  const firstName = extractFirstName(invoice.customerName);

  // TEMPORARY: Simplified payment button for testing
  const paymentButtonHtml = `<a href="${paymentUrl}" target="_blank">Pay Now (Test)</a>`;

  // Function to process HTML and replace template variables
  const processEmailTemplate = (htmlContent: string): string => {
    // Calculate invoice amount as subtotal + tax (excluding processing/platform fees)
    const invoiceAmount = (invoice.subtotal || 0) + (invoice.taxAmount || 0);
    // Replace template variables with actual values
    let processedContent = htmlContent
      .replace(/\{\{firstName\}\}/g, firstName)
      .replace(/\{\{customerName\}\}/g, invoice.customerName)
      .replace(/\{\{invoiceNumber\}\}/g, invoice.invoiceNumber)
      .replace(/\{\{invoiceAmount\}\}/g, `$${invoiceAmount.toFixed(2)}`)
      .replace(/\{\{dueDate\}\}/g, formatDate(invoice.dueDate))
      .replace(/\{\{companyName\}\}/g, companySettings.name || 'Your Company')
      .replace(/\{\{frequency\}\}/g, invoice.recurringDetails?.frequency ? 
        (invoice.recurringDetails.frequency === 'weekly' ? 'Weekly' : 
         invoice.recurringDetails.frequency === 'monthly' ? 'Monthly' : 
         invoice.recurringDetails.frequency === 'quarterly' ? 'Quarterly' : 'Annual') : 'Monthly');
      
    // Regex to find the entire payment button structure generated by Tiptap
    // This looks for a div with data-type="payment-button" and captures its content.
    const tiptapButtonRegex = /<div[^>]*data-type="payment-button"[^>]*>.*?<\/div>/gs;

    // First, handle the {{paymentLink:Custom Text}} format if it exists outside a Tiptap button
    processedContent = processedContent.replace(/\{\{paymentLink:(.*?)\}\}/g, (match, customText) => {
      // If this match is inside a Tiptap button, it will be handled by the next replacement.
      // This replacement is for standalone custom text links.
      if (tiptapButtonRegex.test(match)) {
        return match; // Don't replace if it's part of what tiptapButtonRegex will catch
      }
      return `
        <div style="text-align: center; margin: 30px 0;">
          <a href="${paymentUrl}" 
            target="_blank" 
            rel="noopener noreferrer nofollow" 
            class="payment-button" 
            style="display: inline-block; background-color: #4ADE80; color: white; padding: 12px 32px; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 18px; border: none; text-align: center; min-width: 240px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);">
            ${customText.trim()}
          </a>
        </div>
      `;
    });
    
    // Replace the Tiptap payment button placeholder (div data-type="payment-button") OR the simple {{paymentLink}}
    processedContent = processedContent.replace(tiptapButtonRegex, paymentButtonHtml);
    
    // If there was no Tiptap button, but a simple {{paymentLink}} exists, replace that too.
    // This ensures backward compatibility or cases where the button node wasn't used.
    if (!tiptapButtonRegex.test(htmlContent)) {
      processedContent = processedContent.replace(/\{\{paymentLink\}\}/g, paymentButtonHtml);
    }
    
    return processedContent;
  };

  // Add this console.log before calculating processedEmailContent
  console.log('Data for template processing:', {
    invoiceProp: invoice, // Log the whole invoice object passed as prop
    companySettingsProp: companySettings, // Log the whole companySettings object
    derivedFirstName: firstName,
    customerName: invoice.customerName,
    invoiceNumber: invoice.invoiceNumber,
    invoiceTotal: invoice.total,
    formattedDueDate: formatDate(invoice.dueDate),
    companyName: companySettings.name,
    paymentUrlForButton: paymentUrl,
    rawEmailTemplate: emailTemplate.substring(0, 300) + '...' // Log a snippet of the raw template
  });

  // Add the new console.log for the full emailTemplate before processing
  console.log('Email template BEFORE processing:', emailTemplate);

  // Process the email template with actual data
  const processedEmailContent = processEmailTemplate(emailTemplate);
  console.log('Processed Email Content (snippet):', processedEmailContent.substring(0, 500) + '...');
  
  // Process the email subject with actual data
  const processedSubject = emailSubject
    .replace(/\{\{firstName\}\}/g, firstName)
    .replace(/\{\{customerName\}\}/g, invoice.customerName)
    .replace(/\{\{invoiceNumber\}\}/g, invoice.invoiceNumber)
    .replace(/\{\{companyName\}\}/g, companySettings.name || 'Your Company');

  // Initialize the TipTap editor with the email template
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link,
      Placeholder.configure({
        placeholder: 'Customize your email content...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Image,
      Underline,
    ],
    content: processedEmailContent,
    onUpdate: ({ editor }) => {
      setCustomEmailContent(editor.getHTML());
      setIsCustomized(true);
    },
  });

  // Update editor content when emailPreview changes
  useEffect(() => {
    if (editor && !isCustomized) {
      editor.commands.setContent(processedEmailContent);
    }
  }, [processedEmailContent, editor, isCustomized]);

  // Reset customized state when dialog opens
  useEffect(() => {
    if (open) {
      setIsCustomized(false);
      if (editor) {
        editor.commands.setContent(processedEmailContent);
      }
    }
  }, [open, processedEmailContent, editor]);

  const handleNextClick = () => {
    setActiveTab('email');
  };

  // Process raw HTML content for display
  const cleanHtmlContent = (content: string): string => {
    if (!content) return '';
    
    try {
      // Handle escaped HTML entities
      const cleanedContent = content
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&');
      
      // Fix any broken HTML by parsing it through a DOM element
      const parser = new DOMParser();
      const doc = parser.parseFromString(cleanedContent, 'text/html');
      
      // Get the sanitized HTML
      return doc.body.innerHTML;
    } catch (error) {
      console.error('Error cleaning HTML content:', error);
      return content;
    }
  };

  const handleSendEmail = async () => {
    // Debug invoice data
    console.log("Attempting to send email with invoice:", {
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      email: invoice.customerEmail,
      status: invoice.status
    });

    if (!hasEmailSettings) {
      console.error("Email settings not configured");
      showNotification({
        title: "Error",
        message: "Please configure email settings in the company settings first.",
        type: "error"
      });
      return;
    }

    if (!invoice.id) {
      console.error("Invoice has no ID - must be saved before sending");
      showNotification({
        title: "Error",
        message: "Invoice must be saved before sending. Please save the invoice first.",
        type: "error"
      });
      return;
    }

    try {
      setIsSending(true);
      
      // Use customized content if available, otherwise use the default template
      const processedTemplate = isCustomized ? cleanHtmlContent(customEmailContent) : processedEmailContent;
      
      console.log("Sending email...", {
        to: invoice.customerEmail,
        subject: processedSubject,
        templateLength: processedTemplate.length,
        isCustomized: isCustomized,
        customContentLength: customEmailContent.length || 0
      });
      
      // Send the email with the invoice marked as 'sent'
      const updatedInvoice = await sendInvoiceEmail({ 
        invoice: {
          ...invoice,
          status: 'sent'
        },
        template: processedTemplate,
        subject: processedSubject,
        isCustomContent: isCustomized
      });
      
      // Also update the invoice in the database to ensure status is 'sent'
      // This handles cases where the API might not update the status
      if (invoice.status === 'draft' || invoice.status === 'pending') {
        console.log(`Updating invoice status from ${invoice.status} to sent`);
        await updateInvoice(invoice.id, { status: 'sent' });
        
        // Invalidate the queries to ensure UI updates with new status
        await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });
      }
      
      console.log("Email sent successfully, updated invoice:", updatedInvoice);
      
      // Show small toast notification
      showNotification({
        title: "Success",
        message: `Invoice ${invoice.invoiceNumber} has been sent to ${invoice.customerEmail}`,
        type: "success"
      });
      
      // Show the visual confirmation dialog
      showEmailSentConfirmation({
        invoiceNumber: invoice.invoiceNumber,
        recipientEmail: invoice.customerEmail,
        amount: invoice.total
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to send email:", error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to send invoice. Please try again.",
        type: "error"
      });
    } finally {
      setIsSending(false);
    }
  };

  // Define the email editor toolbar
  const EmailEditorToolbar = () => {
    if (!editor) return null;

    return (
      <div className="border-b p-2 flex items-center flex-wrap gap-1 mb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={editor.isActive('bold') ? 'bg-muted' : ''}
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={editor.isActive('italic') ? 'bg-muted' : ''}
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={editor.isActive('underline') ? 'bg-muted' : ''}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <div className="h-4 w-px bg-border mx-2" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={editor.isActive({ textAlign: 'left' }) ? 'bg-muted' : ''}
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={editor.isActive({ textAlign: 'center' }) ? 'bg-muted' : ''}
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={editor.isActive({ textAlign: 'right' }) ? 'bg-muted' : ''}
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <div className="h-4 w-px bg-border mx-2" />

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={editor.isActive('bulletList') ? 'bg-muted' : ''}
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={editor.isActive('orderedList') ? 'bg-muted' : ''}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const url = window.prompt('URL');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={editor.isActive('link') ? 'bg-muted' : ''}
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            const url = window.prompt('Enter image URL:');
            if (url) {
              editor.chain().focus().setImage({ src: url }).run();
            }
          }}
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const resetEmailContent = () => {
    if (editor) {
      editor.commands.setContent(processedEmailContent);
      setIsCustomized(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Preview & Send Invoice</DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview">Invoice Preview</TabsTrigger>
            <TabsTrigger value="email">Email Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="space-y-4">
            <div className="border rounded-lg overflow-hidden">
              <div className="h-[600px] overflow-y-auto">
                <InvoicePreview 
                  invoice={transformedInvoice} 
                  companySettings={companySettings}
                  showPreviewWatermark={false}
                  isPreviewAndSend={true}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-4">
            <style>
              {`
                .payment-button {
                  display: inline-block !important;
                  background-color: #4ADE80 !important;
                  color: white !important;
                  padding: 12px 32px !important;
                  text-decoration: none !important;
                  border-radius: 6px !important;
                  font-weight: 500 !important;
                  font-size: 18px !important;
                  text-align: center !important;
                  min-width: 240px !important;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
                }
                
                .ProseMirror {
                  padding: 1rem;
                  min-height: 200px;
                  border-radius: 0.5rem;
                  border: 1px solid #e2e8f0;
                  background-color: white;
                }
                
                .ProseMirror:focus {
                  outline: none;
                  border-color: #94a3b8;
                }
                
                .ProseMirror p {
                  margin-bottom: 0.75rem;
                }
                
                .ProseMirror ul, .ProseMirror ol {
                  padding-left: 1.5rem;
                  margin-bottom: 0.75rem;
                }
              `}
            </style>
            <div className="border rounded-lg p-4 bg-gray-50">
              <EmailEditorToolbar />
              {/* Editor content in edit mode */}
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="bg-gray-50 border-b px-4 py-2 text-sm text-gray-600 flex items-center justify-between">
                  <div>
                    <span className="font-medium">To:</span> {invoice.customerEmail}
                  </div>
                  <div>
                    <span className="font-medium">Subject:</span> {processedSubject}
                  </div>
                </div>
                <EditorContent 
                  editor={editor} 
                  className="prose max-w-none [&_a]:text-blue-600 [&_a.payment-button]:text-white hover:[&_a]:underline hover:[&_a.payment-button]:no-underline [&_img]:max-w-full [&_table]:w-full [&_td]:border [&_td]:p-2 [&_th]:border [&_th]:p-2 [&_ul]:list-disc [&_ol]:list-decimal [&_ul,&_ol]:pl-6"
                />
              </div>
              {isCustomized && (
                <div className="flex justify-end mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={resetEmailContent}
                  >
                    Reset to Default
                  </Button>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {!hasEmailSettings && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            <p className="font-medium">Email Settings Not Configured</p>
            <p className="text-sm mt-1">Please configure your SendGrid API key and sender email address in the company settings before sending emails.</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {activeTab === 'preview' ? (
            <Button onClick={handleNextClick}>
              Next
            </Button>
          ) : (
            <Button onClick={handleSendEmail} disabled={isSending || !hasEmailSettings}>
              {isSending ? 'Sending...' : 'Send Invoice'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}